import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../utils/supabase';
import { CartItem } from '../types';
import { useNavigate } from 'react-router-dom';

const Cart: React.FC = () => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [orderNote, setOrderNote] = useState('');
  const { user, signOut } = useAuth();
  const { language, setLanguage, t, isRTL } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCartItems();
  }, []);

  const fetchCartItems = async () => {
    const { data } = await supabase
      .from('habib_cart')
      .select(`
        *,
        product:habib_products(*, category:habib_categories(*)),
        menu:habib_menu(*, menu_category:habib_menu_categories(*))
      `)
      .eq('user_id', user?.id);
    if (data) setCartItems(data);
  };

  const updateQuantity = async (itemId: number, quantity: number) => {
    if (quantity <= 0) {
      await supabase.from('habib_cart').delete().eq('id', itemId);
      setCartItems(cartItems.filter(item => item.id !== itemId));
    } else {
      await supabase.from('habib_cart').update({ quantity }).eq('id', itemId);
      setCartItems(cartItems.map(item => 
        item.id === itemId ? { ...item, quantity } : item
      ));
    }
  };

  const createOrder = async () => {
    if (cartItems.length === 0) return;

    const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    
    const { data: order } = await supabase
      .from('habib_orders')
      .insert([{
        user_id: user?.id,
        status: 'Permintaan Pesanan',
        total_items: totalItems,
        note: orderNote || null
      }])
      .select()
      .single();

    if (order) {
      const orderItems = cartItems.map(item => ({
        order_id: order.id,
        product_id: item.product_id || null,
        menu_id: item.menu_id || null,
        quantity: item.quantity
      }));

      await supabase.from('habib_order_items').insert(orderItems);
      await supabase.from('habib_cart').delete().eq('user_id', user?.id);
      
      setCartItems([]);
      setOrderNote('');
      navigate('/orders');
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
      <nav className="bg-white/80 backdrop-blur-md shadow-soft" style={{borderBottom: '1px solid #05472A20'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${isRTL ? 'ml-3' : 'mr-3'} flex items-center justify-center shadow-lg overflow-hidden`} style={{backgroundColor: '#05472A'}}>
                <img 
                  src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/logo-habib-1.png"
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-lg sm:text-xl font-bold" style={{color: '#05472A'}}>{t('shoppingCart')}</h1>
            </div>
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 sm:space-x-4`}>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setLanguage('en')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all duration-300 ${
                    language === 'en' ? 'text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={language === 'en' ? {backgroundColor: '#05472A'} : {}}
                >
                  EN
                </button>
                <button 
                  onClick={() => setLanguage('ar')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all duration-300 ${
                    language === 'ar' ? 'text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={language === 'ar' ? {backgroundColor: '#05472A'} : {}}
                >
                  AR
                </button>
              </div>
              <button onClick={() => navigate('/')} className="bg-white hover:bg-green-50 border px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium" style={{color: '#05472A', borderColor: '#05472A40'}}>
                <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${isRTL ? 'ml-1 sm:ml-2 rotate-180' : 'mr-1 sm:mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="hidden sm:inline">{t('back')}</span>
              </button>
              <button onClick={signOut} className="bg-white hover:bg-green-50 border px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium" style={{color: '#05472A', borderColor: '#05472A40'}}>
                <span className="hidden sm:inline">{t('signOut')}</span>
                <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
        {cartItems.length === 0 ? (
          <div className="card-elevated p-8 sm:p-12 text-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-slate-200 to-slate-300 rounded-full mx-auto mb-6 flex items-center justify-center">
              <svg className="w-10 h-10 sm:w-12 sm:h-12 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 11-4 0v-6m4 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
              </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold mb-2" style={{color: '#05472A'}}>{t('yourCartIsEmpty')}</h2>
            <p className="mb-6" style={{color: '#05472A99'}}>{t('addSomeProducts')}</p>
            <button onClick={() => navigate('/')} className="text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium transform hover:scale-105 active:scale-95" style={{backgroundColor: '#05472A'}}>
              {t('startShopping')}
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
              {cartItems.map((item) => {
                const isProduct = !!item.product_id;
                const isMenu = !!item.menu_id;
                const photo = isProduct ? item.product?.photo : item.menu?.photo;
                const name = isProduct ? item.product?.nama_produk : item.menu?.name;
                const description = isProduct ? item.product?.deskripsi_produk : item.menu?.description;
                
                return (
                  <div key={item.id} className="card-elevated p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="relative">
                        <img 
                          src={photo} 
                          alt={name}
                          className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-xl"
                        />
                        <div className={`absolute -bottom-1 -left-1 px-2 py-0.5 rounded-md text-xs font-semibold ${
                          isMenu ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'
                        }`}>
                          {isMenu ? '🍽️' : '📦'}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold mb-1 text-sm sm:text-base" style={{color: '#05472A'}}>{name}</h3>
                        <p className="text-xs sm:text-sm line-clamp-2" style={{color: '#05472A99'}}>{description}</p>
                        {isMenu && item.menu?.menu_category && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                            {item.menu.menu_category.name}
                          </span>
                        )}
                        {isProduct && item.product?.category && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {item.product.category.name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4">
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110 active:scale-95 bg-slate-100 hover:bg-slate-200" style={{color: '#05472A'}}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                        </button>
                        <span className="w-8 sm:w-10 text-center font-semibold text-sm sm:text-base" style={{color: '#05472A'}}>{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110 active:scale-95" style={{backgroundColor: '#05472A20', color: '#05472A'}}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Order Note */}
            <div className="card-elevated p-4 sm:p-6 mb-6">
              <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>
                Order Note (Optional)
              </label>
              <textarea
                value={orderNote}
                onChange={(e) => setOrderNote(e.target.value)}
                placeholder="Add any special instructions or notes for your order..."
                className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300 resize-none"
                style={{borderColor: '#05472A20'}}
                rows={3}
                maxLength={500}
              />
              <p className="text-xs mt-1" style={{color: '#05472A99'}}>
                {orderNote.length}/500 characters
              </p>
            </div>
            
            <div className="card-elevated p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className={`text-center ${isRTL ? 'sm:text-right' : 'sm:text-left'}`}>
                  <p className="text-lg sm:text-xl font-bold" style={{color: '#05472A'}}>
                    {t('totalItems')}: {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                  </p>
                  <p className="text-sm" style={{color: '#05472A99'}}>{t('readyToCheckout')}</p>
                </div>
                <button onClick={createOrder} className="text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium transform hover:scale-105 active:scale-95 w-full sm:w-auto" style={{backgroundColor: '#05472A'}}>
                  <svg className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('placeOrder')}
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Cart;
